import React, { useRef, useCallback, useState } from 'react';
import { IconButton } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useDispatch, useSelector } from 'react-redux';
import { setHighlightedItem, clearHighlightedItem, selectHighlightedItem } from '../features/highlightSlice';

/**
 * A reusable eye icon component that highlights corresponding items in the Cart Composer
 * 
 * @param {Object} props Component props
 * @param {string} props.itemId The ID of the item to highlight
 * @param {Object} props.metadata Additional data for complex matching scenarios
 * @param {Object} props.sx Additional styling for the icon button
 */
const HighlightEyeIcon = ({ 
  itemId, 
  metadata = null,
  sx = {}
}) => {
  const dispatch = useDispatch();
  const highlightedItemId = useSelector(selectHighlightedItem);
  const isActive = highlightedItemId === itemId;
  
  // Use a ref to track the hover timer
  const hoverTimerRef = useRef(null);
  
  // Add local hover state to handle the visual effect independently
  const [isLocalHover, setIsLocalHover] = useState(false);
  
  // Debounced mouse enter handler with 100ms delay
  const handleMouseEnter = useCallback(() => {
    // Immediately update the local hover state for visual feedback
    setIsLocalHover(true);
    
    // Clear any existing timer
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }
    
    // Set a new timer with 100ms delay for the actual highlight action
    hoverTimerRef.current = setTimeout(() => {
      dispatch(setHighlightedItem({
        itemId,
        type: 'hover',
        source: 'invoice',
        metadata,
        shouldScroll: true
      }));
    }, 100); // 0.1 second delay
  }, [dispatch, itemId, metadata]);
  
  const handleMouseLeave = useCallback(() => {
    // Immediately update the local hover state
    setIsLocalHover(false);
    
    // Clear the timer if mouse leaves before the delay completes
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    
    dispatch(clearHighlightedItem());
  }, [dispatch]);
  
  const handleClick = useCallback((e) => {
    // Prevent the click from propagating to parent elements
    e.stopPropagation();
    
    // Clear any hover timer to prevent delayed hover effect after click
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    
    // If already highlighted, clear it, otherwise set it
    if (isActive) {
      dispatch(clearHighlightedItem());
    } else {
      dispatch(setHighlightedItem({
        itemId,
        type: 'click',
        source: 'invoice',
        metadata,
        shouldScroll: true
      }));
    }
  }, [dispatch, isActive, itemId, metadata]);
  
  // Determine the visual state based on both Redux state and local hover state
  const isVisuallyActive = isActive || isLocalHover;
  
  return (
    <IconButton
      sx={{
        color: isVisuallyActive ? 'rgba(2, 129, 192, 1)' : 'rgba(2, 129, 192, 0.7)',
        padding: '2px',
        backgroundColor: isVisuallyActive ? 'rgba(2, 129, 192, 0.1)' : 'transparent',
        '&:hover': {
          color: 'rgba(2, 129, 192, 1)',
          backgroundColor: 'rgba(2, 129, 192, 0.1)',
        },
        ...sx
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      size="small"
    >
      <VisibilityIcon fontSize="small" />
    </IconButton>
  );
};

export default HighlightEyeIcon;